(() => {
  const root = document.documentElement;

  const mobileHeaderQuery = window.matchMedia('(max-width: 1022px)');
  let compactStart = 80;
  let compactSnap = 120;
  let measureFrame = null;
  let isHeaderScrollCompensating = false;

  const getHeaderFlowElement = () => {
    const candidates = [
      document.querySelector('.site-header-wrapper'),
      document.querySelector('[data-section-type="static-header"]'),
      document.querySelector('.site-header'),
    ].filter(Boolean);

    return candidates.find((element) => {
      const rect = element.getBoundingClientRect();
      return getComputedStyle(element).display !== 'contents' && rect.height > 0;
    }) || candidates.at(-1) || null;
  };

  const getHeaderSnapAnchor = () =>
    document.querySelector(
      '.product-main, .product--outer, [data-section-type="static-product"], .productgrid--outer, #MainContent, main',
    );

  const updateHeaderState = () => {
    if (!mobileHeaderQuery.matches) {
      root.classList.remove('cm-header-controls-exiting', 'cm-header-scrolled');
      return;
    }

    const isExiting = window.scrollY >= compactStart && window.scrollY < compactSnap;
    const isCompact = window.scrollY >= compactSnap;
    const wasCompact = root.classList.contains('cm-header-scrolled');
    const shouldCompensate = wasCompact !== isCompact && !isHeaderScrollCompensating;
    const anchor = shouldCompensate ? getHeaderSnapAnchor() : null;
    const beforeTop = anchor?.getBoundingClientRect().top;

    root.classList.toggle('cm-header-controls-exiting', isExiting);
    root.classList.toggle('cm-header-scrolled', isCompact);

    if (anchor && Number.isFinite(beforeTop)) {
      const afterTop = anchor.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;

      if (Math.abs(delta) > 1) {
        isHeaderScrollCompensating = true;
        window.scrollBy(0, delta);

        window.requestAnimationFrame(() => {
          isHeaderScrollCompensating = false;
        });
      }
    }
  };

  const measureHeaderScrollPoints = () => {
    if (!mobileHeaderQuery.matches) {
      compactStart = Number.POSITIVE_INFINITY;
      compactSnap = Number.POSITIVE_INFINITY;
      updateHeaderState();
      return;
    }

    const hadExiting = root.classList.contains('cm-header-controls-exiting');
    const hadCompact = root.classList.contains('cm-header-scrolled');

    root.classList.remove('cm-header-controls-exiting', 'cm-header-scrolled');

    const header = getHeaderFlowElement();
    const controlRow = document.querySelector('#cm-mobile-garage') || document.querySelector('.live-search');
    const headerRect = header?.getBoundingClientRect();
    const rect = controlRow?.getBoundingClientRect();

    if (headerRect) {
      root.style.setProperty('--cm-mobile-header-flow-height', `${Math.round(headerRect.height)}px`);
    }

    if (rect) {
      compactSnap = Math.max(0, Math.round(window.scrollY + rect.top));
      compactStart = Math.max(0, Math.round(window.scrollY + (headerRect?.top ?? rect.top - 80)));

      if (compactStart >= compactSnap) {
        compactStart = Math.max(0, compactSnap - 1);
      }
    } else {
      compactStart = 80;
      compactSnap = 120;
    }

    root.classList.toggle('cm-header-controls-exiting', hadExiting);
    root.classList.toggle('cm-header-scrolled', hadCompact);
    updateHeaderState();
  };

  const scheduleHeaderMeasure = () => {
    if (measureFrame) return;

    measureFrame = window.requestAnimationFrame(() => {
      measureFrame = null;
      measureHeaderScrollPoints();
    });
  };

  scheduleHeaderMeasure();
  window.addEventListener('load', scheduleHeaderMeasure);
  window.addEventListener('scroll', updateHeaderState, { passive: true });
  window.addEventListener('resize', scheduleHeaderMeasure);
  mobileHeaderQuery.addEventListener?.('change', scheduleHeaderMeasure);
  window.setTimeout(scheduleHeaderMeasure, 500);
  window.setTimeout(scheduleHeaderMeasure, 1500);

  const updateSearchPlaceholders = () => {
    document
      .querySelectorAll('.cm_searchInput, .live-search input, input[type="search"], input[name="q"]')
      .forEach((input) => {
        if (input.matches('[data-parity-product-type-v3-search]')) return;
        input.setAttribute('placeholder', 'Search');
        input.setAttribute('aria-label', 'Search');
      });
  };

  const updateGarageLabels = () => {
    document
      .querySelectorAll('#cm-garage [title], #cm-mobile-garage [title]')
      .forEach((element) => {
        element.removeAttribute('title');
      });
  };

  const meteorPartsCategoryLinks = new Map([
    ['parts', '/collections/motorcycle-parts'],
    ['shop all', '/collections/motorcycle-parts'],
    ['shop by category', '/collections/motorcycle-parts'],
    ['air cleaners', '/collections/motorcycle-air-cleaners'],
    ['audio & speakers', '/collections/motorcycle-audio'],
    ['brakes', '/collections/motorcycle-brakes'],
    ['dashes & gauges', '/collections/motorcycle-dash-gauges'],
    ['dash & gauges', '/collections/motorcycle-dash-gauges'],
    ['drive & transmission', '/collections/motorcycle-drive-transmission'],
    ['electrical', '/collections/motorcycle-batteries-electrical'],
    ['batteries & electrical', '/collections/motorcycle-batteries-electrical'],
    ['engine', '/collections/motorcycle-engine'],
    ['exhaust', '/collections/motorcycle-exhaust'],
    ['fenders', '/collections/motorcycle-fenders-extenders'],
    ['fenders & license plate frames', '/collections/motorcycle-body-parts'],
    ['body, fairing & fender', '/collections/motorcycle-body-parts'],
    ['footpegs & foot controls', '/collections/motorcycle-foot-controls'],
    ['footrests & floorboards', '/collections/motorcycle-foot-controls'],
    ['foot controls', '/collections/motorcycle-foot-controls'],
    ['fuel & air systems', '/collections/motorcycle-air-fuel'],
    ['air & fuel', '/collections/motorcycle-air-fuel'],
    ['fuel management', '/collections/motorcycle-fuel-management'],
    ['gaskets & seals', '/collections/motorcycle-gaskets'],
    ['handlebars controls & mirrors', '/collections/motorcycle-hand-controls-handlebars'],
    ['handlebars & controls', '/collections/motorcycle-hand-controls-handlebars'],
    ['handlebars', '/collections/motorcycle-handlebars'],
    ['lighting', '/collections/motorcycle-lighting'],
    ['oil filters & gauges', '/collections/motorcycle-oil-filters'],
    ['tanks & oil filters', '/collections/motorcycle-gas-oil-tanks'],
    ['gas & oil tanks', '/collections/motorcycle-gas-oil-tanks'],
    ['transmission & driveline', '/collections/motorcycle-drive-transmission'],
    ['suspension', '/collections/motorcycle-suspension'],
    ['frames & suspension', '/collections/motorcycle-suspension'],
    ['suspension & frame', '/collections/motorcycle-suspension'],
    ['wheels', '/collections/motorcycle-wheel-axle'],
    ['wheel & axle', '/collections/motorcycle-wheel-axle'],
    ['wheels & axles', '/collections/motorcycle-wheel-axle'],
    ['windshields & fairings', '/collections/motorcycle-windshields-windscreens'],
    ['windshields & windscreens', '/collections/motorcycle-windshields-windscreens'],
    ['seats & sissy bars', '/collections/motorcycle-seating'],
  ]);

  const normalizeMeteorNavText = (value) =>
    String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const getDirectMeteorNavLink = (item) =>
    Array.from(item?.children || []).find((child) =>
      child.matches?.('a[href], button, [role="button"], [data-href]'),
    ) || item?.querySelector?.('a[href], button, [role="button"], [data-href]');

  const rewriteMeteorNavAnchor = (anchor, forcedLabel) => {
    if (!anchor?.matches?.('a[href]')) return;

    const normalizedLabel = normalizeMeteorNavText(forcedLabel || anchor.textContent);
    const targetUrl = meteorPartsCategoryLinks.get(normalizedLabel);

    if (targetUrl && anchor.getAttribute('href') !== targetUrl) {
      anchor.setAttribute('href', targetUrl);
    }
  };

  const isMeteorPartsDesktopMenu = (menu) =>
    Array.from(menu?.querySelectorAll?.('[role="group"][aria-label], .m-nav[aria-label]') || []).some(
      (group) => normalizeMeteorNavText(group.getAttribute('aria-label')) === 'parts',
    );

  const updateMeteorPartsNavigation = () => {
    const nav = document.querySelector('#meteor-desktop-nav');
    if (!nav) return;

    const topLevelItems = Array.from(nav.children).filter((child) => child.matches?.('li'));
    const partsItem =
      topLevelItems.find((item) => normalizeMeteorNavText(getDirectMeteorNavLink(item)?.textContent) === 'parts') ||
      topLevelItems[3];

    if (!partsItem) return;

    rewriteMeteorNavAnchor(getDirectMeteorNavLink(partsItem), 'parts');
    partsItem.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor));

    document
      .querySelectorAll(
        [
          '.Meteor-MobileMenu[aria-label="Parts"] a[href]',
          '.Meteor-Drawer__ItemList[aria-label="Parts"] a[href]',
          '[role="menu"][aria-label="Parts"] a[href]',
        ].join(','),
      )
      .forEach((anchor) => rewriteMeteorNavAnchor(anchor));

    document.querySelectorAll('.meteor-menu').forEach((menu) => {
      if (!isMeteorPartsDesktopMenu(menu)) return;
      menu.querySelectorAll('a[href]').forEach((anchor) => rewriteMeteorNavAnchor(anchor));
    });
  };

  const updateConvermaxHeaderControls = () => {
    updateSearchPlaceholders();
    updateGarageLabels();
    updateMeteorPartsNavigation();
  };

  updateConvermaxHeaderControls();
  document.addEventListener('DOMContentLoaded', updateConvermaxHeaderControls);

  if ('MutationObserver' in window) {
    new MutationObserver(updateConvermaxHeaderControls).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();

window.parityGarageRideCycleStates = [
  { id: 'harley-touring', label: 'Harley Touring' },
  { id: 'harley-sportster', label: 'Harley Sportster' },
  { id: 'harley-trike', label: 'Harley Trike' },
  { id: 'harley-cruiser', label: 'Harley Cruiser' },
  { id: 'dirtbike', label: 'Dirtbike' },
  { id: 'utv', label: 'UTV' },
  { id: 'atv', label: 'ATV' },
];
